import type {
  AgentLayoutSettingsOptions,
  DatabaseAgentContract,
  UpdateAiFieldRunMetadataOptions,
  WriteAiSummaryCellOptions
} from '@databases/ai-first-creation-database-agent-types';
import { type MutableDatabase } from '@databases/model/database';
import {
  type DatabaseEntry,
  type DatabaseField,
  type DatabaseFieldType,
  type NumberFormat
} from '@databases/types/database';
import type {
  DatabaseFilter,
  DatabaseLayoutType,
  DatabaseView
} from '@databases/types/database/view';

import { AiFieldAgent } from './ai-field-agent.ts';
import { EntryAgent } from './entry-agent.ts';
import { FieldAgent } from './field-agent.ts';
import { ValueAgent } from './value-agent.ts';
import { type LayoutSettingsOptions, ViewAgent } from './view-agent.ts';

export class DatabaseAgent implements DatabaseAgentContract {
  private database: MutableDatabase;
  private readonly fieldAgent: FieldAgent;
  private readonly entryAgent: EntryAgent;
  private readonly viewAgent: ViewAgent;
  private readonly valueAgent: ValueAgent;
  private readonly aiFieldAgent: AiFieldAgent;

  constructor(database: MutableDatabase) {
    this.database = database;
    this.fieldAgent = new FieldAgent(database);
    this.entryAgent = new EntryAgent(database);
    this.viewAgent = new ViewAgent(database);
    this.valueAgent = new ValueAgent(database);
    this.aiFieldAgent = new AiFieldAgent(database);
  }

  public addField(field: DatabaseField, index?: number): void {
    return this.fieldAgent.addField(field, index);
  }

  public setFieldName(fieldName: string, newName: string): void {
    return this.fieldAgent.setFieldName(fieldName, newName);
  }

  public setFieldType(fieldName: string, type: DatabaseFieldType): void {
    return this.fieldAgent.setFieldType(fieldName, type);
  }

  public setFieldMultiple(fieldName: string, multiple: boolean): void {
    return this.fieldAgent.setFieldMultiple(fieldName, multiple);
  }

  public setFieldNumberFormat(fieldName: string, numberFormat: NumberFormat): void {
    return this.fieldAgent.setFieldNumberFormat(fieldName, numberFormat);
  }

  public setFieldDescription(fieldName: string, description: string): void {
    return this.fieldAgent.setFieldDescription(fieldName, description);
  }

  public setDependentFieldId(fieldName: string, dependentFieldName: string): void {
    const dependentFieldId = this.fieldAgent.getFieldIdByName(dependentFieldName);
    if (!dependentFieldId) {
      throw new Error(`Dependent field with name "${dependentFieldName}" not found`);
    }
    return this.fieldAgent.setDependentFieldId(fieldName, dependentFieldId);
  }

  public setDefaultEnabled(fieldName: string, defaultEnabled: boolean): void {
    return this.fieldAgent.setDefaultEnabled(fieldName, defaultEnabled);
  }

  public removeField(fieldId: string): void {
    return this.fieldAgent.removeField(fieldId);
  }

  public getCloudId(fieldId: string): string | undefined {
    return this.fieldAgent.getCloudId(fieldId);
  }

  public setCloudId(fieldId: string, cloudId: string): void {
    return this.fieldAgent.setCloudId(fieldId, cloudId);
  }

  public addEntry(entry: DatabaseEntry, index?: string): void {
    return this.entryAgent.addEntry(entry, index);
  }

  public removeEntry(entryId: string): void {
    return this.entryAgent.removeEntry(entryId);
  }

  public addView(view: DatabaseView): void {
    return this.viewAgent.addView(view);
  }

  public deleteView(viewId: string): void {
    return this.viewAgent.deleteView(viewId);
  }

  public isViewsEmpty(): boolean {
    return this.viewAgent.isViewsEmpty();
  }

  public getViewCount(): number {
    return this.viewAgent.getViewCount();
  }

  public setDefaultView(viewId: string): void {
    return this.viewAgent.setDefaultView(viewId);
  }

  public updateViewLayout(viewId: string, settings: AgentLayoutSettingsOptions): void {
    return this.viewAgent.updateViewLayout(viewId, settings as LayoutSettingsOptions);
  }

  public addSortToView(viewId: string, fieldId: string, ascending: boolean): void {
    return this.viewAgent.addSortToView(viewId, fieldId, ascending);
  }

  public updateSort(viewId: string, fieldId: string, ascending: boolean): void {
    return this.viewAgent.updateSort(viewId, fieldId, ascending);
  }

  public removeSort(viewId: string, fieldId: string): void {
    return this.viewAgent.removeSort(viewId, fieldId);
  }

  public hasSortForField(viewId: string, fieldId: string): boolean {
    return this.viewAgent.hasSortForField(viewId, fieldId);
  }

  public setViewFilter(viewId: string, filter: DatabaseFilter): void {
    return this.viewAgent.setFilter(viewId, filter);
  }

  public clearViewFilter(viewId: string): void {
    return this.viewAgent.clearViewFilter(viewId);
  }

  public getViewIdByName(name: string): string | undefined {
    return this.viewAgent.getViewIdByName(name);
  }

  public getViewLayoutType(viewId: string): DatabaseLayoutType | undefined {
    return this.viewAgent.getViewLayoutType(viewId);
  }

  public updateViewName(viewName: string, newName: string): void {
    return this.viewAgent.updateViewName(viewName, newName);
  }

  public reorderView(viewId: string, newIndex: number): void {
    return this.viewAgent.reorderView(viewId, newIndex);
  }

  public getFieldIdByName(name: string): string | undefined {
    return this.fieldAgent.getFieldIdByName(name);
  }

  public getFieldById(fieldId: string): DatabaseField | undefined {
    return this.fieldAgent.getFieldById(fieldId);
  }

  public findExistingSingleSelectField(): DatabaseField | undefined {
    return this.fieldAgent.findExistingSingleSelectField();
  }

  public setValue(
    entryId: string,
    fieldId: string,
    value: any,
    resetValue = false
  ): void {
    return this.valueAgent.setValue(entryId, fieldId, value, resetValue);
  }

  public writeAiSummaryCell(options: WriteAiSummaryCellOptions): void {
    return this.aiFieldAgent.writeAiSummaryCell(options);
  }

  public updateAiFieldRunMetadata(options: UpdateAiFieldRunMetadataOptions): void {
    return this.aiFieldAgent.updateAiFieldRunMetadata(options);
  }

  public clearValue(entryId: string, fieldId: string): void {
    return this.valueAgent.clearValue(entryId, fieldId);
  }

  public getOrCreateSelectOption(fieldId: string, optionValue: string): string {
    return this.fieldAgent.getOrCreateSelectOption(fieldId, optionValue);
  }

  public hideFields(viewId: string, fieldIds: string[]): void {
    return this.viewAgent.hideFields(viewId, fieldIds);
  }

  public showFields(viewId: string, fieldIds: string[]): void {
    return this.viewAgent.showFields(viewId, fieldIds);
  }

  public clearDatabase(): void {
    this.database.mutate((db) => {
      const entryIds = [...db.entries];
      const fieldIds = [...db.fields];
      const viewIds = [...db.views];

      entryIds.forEach((entryId) => {
        db.deleteEntry(entryId);
      });

      fieldIds.forEach((fieldId) => {
        db.deleteField(fieldId);
      });

      viewIds.forEach((viewId) => {
        db.deleteView(viewId);
      });
    });
  }
}
